import React, { useState } from 'react';
import { updatePassword, deleteUser } from 'firebase/auth';
import ReauthModal from './ReauthModal';
import { auth, db } from '../firebase';
import { updateDoc, deleteDoc, doc } from 'firebase/firestore';
import ChangePasswordModal from './ChangePasswordModal';
import DeleteAccountModal from './DeleteAccountModal';
import { playSound, SOUNDS } from '../utils/audio';
import './UserProfile.css';
import CancelSubscriptionModal from './CancelSubscriptionModal';

const UserProfile = ({ user, userData, loading }) => {
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showReauthModal, setShowReauthModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const handleChangePassword = async (newPassword) => {
    setPendingAction(() => async () => {
      try {
        await updatePassword(auth.currentUser, newPassword);
        playSound(SOUNDS.SUCCESS);
        setMessage('Password updated successfully');
        setShowPasswordModal(false);
      } catch (passError) {
        setError('Error updating password');
      }
    });
    setShowReauthModal(true);
  };

  const handleCancelSubscription = async () => {
    try {
      // Changed from Netlify function to new backend
      const response = await fetch('https://backend.hanzi-flashcards.com/api/subscription/cancel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await auth.currentUser.getIdToken()}`
        },
        body: JSON.stringify({
          subscriptionId: userData.subscriptionId,
          userId: user.uid
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to cancel subscription');
      }

      await updateDoc(doc(db, 'users', user.uid), {
        cancellationRequested: true,
        cancellationDate: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });

      setMessage('Your subscription has been cancelled. You will continue to have access until the end of your current billing period.');
      playSound(SOUNDS.SUCCESS);
      setShowCancelModal(false);
    } catch (cancelError) {
      console.error('Error cancelling subscription:', cancelError);
      setError('Error cancelling subscription. Please try again or contact support.');
    }
  };

  const handleDeleteAccount = async () => {
    setPendingAction(() => async () => {
      try {
        // Check if user has active subscription
        if (userData?.subscriptionId) {
          // Cancel subscription with PayProGlobal first
          const response = await fetch('https://backend.hanzi-flashcards.com/api/subscription/cancel', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${await auth.currentUser.getIdToken()}`
            },
            body: JSON.stringify({
              subscriptionId: userData.subscriptionId,
              userId: user.uid
            })
          });

          if (!response.ok) {
            throw new Error('Failed to cancel subscription');
          }
        }

        // Delete user data and account using Firebase v11 syntax
        await deleteDoc(doc(db, 'users', user.uid));
        await deleteUser(auth.currentUser);
      } catch (deleteAccountError) {
        setError('Error deleting account');
      }
    });
    setShowReauthModal(true);
  };

  const handleReauthSuccess = async () => {
    setShowReauthModal(false);
    if (pendingAction) {
      await pendingAction();
      setPendingAction(null);
    }
  };

  if (loading) {
    return <div className="profile-container">Loading...</div>;
  }

  const handleSubscribeMonthly = () => {
    window.open('https://store.payproglobal.com/checkout?products[1][id]=104742', '_blank', 'noopener');
  };

  const handleSubscribeAnnual = () => {
    window.open('https://store.payproglobal.com/checkout?products[1][id]=104239', '_blank', 'noopener');
  };

  return (
    <div className="profile-container">
      {error && <div className="error">{error}</div>}
      {message && <div className="success">{message}</div>}
      <h1>User Profile</h1>

      <div className="profile-section">
        <h2>Account Details</h2>
        <p><strong>Email:</strong> {user.email}</p>
      </div>

      <div className="profile-section">
        <h2>Subscription</h2>
        <div className="subscription-info">
          <p><strong>Current Tier:</strong> {userData?.tier || 'Free'}</p>
          {(!userData?.tier || userData?.tier === 'free') && (
            <div className="upgrade-section">
              <h3>Upgrade Your Account</h3>
              <div className="subscription-options">
                <div className="subscription-card">
                  <h4>Monthly Plan</h4>
                  <p className="price">$4.99<span className="period">/month</span></p>
                  <ul>
                    <li>Unlimited sets</li>
                    <li>Unlimited cards per set</li>
                    <li>All features included</li>
                  </ul>
                  <button className="btn btn-primary" onClick={() => {
                    playSound(SOUNDS.TAP);
                    handleSubscribeMonthly();
                  }}>
                    Subscribe Monthly
                  </button>
                </div>
                <div className="subscription-card">
                  <h4>Annual Plan</h4>
                  <p className="price">$29.99<span className="period">/year</span></p>
                  <p className="savings">Save 50%</p>
                  <ul>
                    <li>Unlimited sets</li>
                    <li>Unlimited cards per set</li>
                    <li>All features included</li>
                  </ul>
                  <button className="btn btn-primary" onClick={() => {
                    playSound(SOUNDS.TAP);
                    handleSubscribeAnnual();
                  }}>
                    Subscribe Annually
                  </button>
                </div>
              </div>
            </div>
          )}
          {userData?.tier && userData.tier !== 'free' && (
            <div className="subscription-details">
              <p><strong>Next Payment:</strong> {new Date(userData.subscriptionEndDate).toLocaleDateString()}</p>
              <p><strong>Amount:</strong> ${userData.nextBillingAmount} {userData.nextBillingCurrency}</p>
              {userData.lastFourDigits && (
                <p><strong>Card:</strong> **** **** **** {userData.lastFourDigits}</p>
              )}
              {userData.tier === 'monthly' && (
                <div>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      playSound(SOUNDS.TAP);
                      handleSubscribeAnnual();
                    }}
                    style={{ marginTop: '15px' }}
                  >
                    Switch to Annual Plan (Save 50%)
                  </button>
                  <p className="upgrade-note">
                    When upgrading, your new annual subscription will start from your current monthly end date
                  </p>
                </div>
              )}
              <button
                className="btn btn-danger"
                onClick={() => {
                  playSound(SOUNDS.TAP);
                  setShowCancelModal(true);
                }}
                style={{ marginTop: '15px' }}
              >
                Cancel Subscription
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="profile-section">
        <h2>Account Management</h2>
        <div className="management-buttons">
          <button
            className="btn btn-secondary"
            onClick={() => {
              playSound(SOUNDS.TAP);
              setShowPasswordModal(true);
            }}
          >
            Change Password
          </button>
          <button
            className="btn btn-danger"
            onClick={() => {
              playSound(SOUNDS.TAP);
              setShowDeleteModal(true);
            }}
          >
            Delete Account
          </button>
        </div>
      </div>

      {showPasswordModal && (
        <ChangePasswordModal
          onClose={() => setShowPasswordModal(false)}
          onSubmit={handleChangePassword}
        />
      )}

      {showDeleteModal && (
        <DeleteAccountModal
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteAccount}
        />
      )}

      {showReauthModal && (
        <ReauthModal
          onClose={() => {
            setShowReauthModal(false);
            setPendingAction(null);
          }}
          onSuccess={handleReauthSuccess}
        />
      )}

      {showCancelModal && (
        <CancelSubscriptionModal
          onClose={() => setShowCancelModal(false)}
          onConfirm={() => {
            handleCancelSubscription();
            setShowCancelModal(false);
          }}
        />
      )}
    </div>
  );
};

export default UserProfile;